import {
  getList,
  getString,
  getOptionalString,
  getLeoDate,
  LeoDate,
  getOptionalLeoDate,
  getObject,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { GetProjectSpocsSortOrder, GetProjectSpocsSortOrderDTO, GetProjectSpocsSortOrderEnums } from "./getProjectSpocsSortOrder";
import { SpocManagementItemsPerPage, SpocManagementItemsPerPageDTO } from "./spocManagementItemsPerPage";
import { SpocManagementPageIndex, SpocManagementPageIndexDTO } from "./spocManagementPageIndex";
import { GetProjectSpocsPaginationResponse, GetProjectSpocsPaginationResponseDTO } from "./getProjectSpocsPaginationResponse";
/* eslint-disable import/export */
export abstract class GetProjectSpocsRPC implements
  LeoRPC<GetProjectSpocsRPC.Request, GetProjectSpocsRPC.Response, GetProjectSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: GetProjectSpocsRPC.Request
  ): Promise<LeoRPCResult<GetProjectSpocsRPC.Response, GetProjectSpocsRPC.Errors.Errors>>;
}

export namespace GetProjectSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    sortList: GetProjectSpocsSortOrderDTO[];
    name?: string | null;
    emailAddress?: string | null;
    projectId?: string | null;
    addedOnStartDate?: string | null;
    addedOnEndDate?: string | null;
    expiresOnStartDate?: string | null;
    expiresOnEndDate?: string | null;
    itemsPerPage: SpocManagementItemsPerPageDTO;
    pageIndex: SpocManagementPageIndexDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetProjectSpocsRPCRequest: boolean = true;
  
    readonly sortList: GetProjectSpocsSortOrder[];
    readonly name: string | null;
    readonly emailAddress: string | null;
    readonly projectId: string | null;
    readonly addedOnStartDate: LeoDate | null;
    readonly addedOnEndDate: LeoDate | null;
    readonly expiresOnStartDate: LeoDate | null;
    readonly expiresOnEndDate: LeoDate | null;
    readonly itemsPerPage: SpocManagementItemsPerPage;
    readonly pageIndex: SpocManagementPageIndex;
  
    static readonly constraints = {
      "name": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "emailAddress": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "projectId": {
        "minSize": 3,
        "maxSize": 200,
        
        
        
        
      },
    }
  
    constructor(
    sortList: GetProjectSpocsSortOrder[], 
    name: string | null = null, 
    emailAddress: string | null = null, 
    projectId: string | null = null, 
    addedOnStartDate: LeoDate | null = null, 
    addedOnEndDate: LeoDate | null = null, 
    expiresOnStartDate: LeoDate | null = null, 
    expiresOnEndDate: LeoDate | null = null, 
    itemsPerPage: SpocManagementItemsPerPage, 
    pageIndex: SpocManagementPageIndex
    ) {
      this.sortList = sortList;
      if ( name !== undefined && name !== null) {
        Request.validateName(name);
      }
      this.name = name;
      if ( emailAddress !== undefined && emailAddress !== null) {
        Request.validateEmailAddress(emailAddress);
      }
      this.emailAddress = emailAddress;
      if ( projectId !== undefined && projectId !== null) {
        Request.validateProjectId(projectId);
      }
      this.projectId = projectId;
      this.addedOnStartDate = addedOnStartDate;
      this.addedOnEndDate = addedOnEndDate;
      this.expiresOnStartDate = expiresOnStartDate;
      this.expiresOnEndDate = expiresOnEndDate;
      this.itemsPerPage = itemsPerPage;
      this.pageIndex = pageIndex;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const sortList = getList<GetProjectSpocsSortOrderDTO, GetProjectSpocsSortOrder>(
        dto,
        "sortList",
        GetProjectSpocsSortOrder.fromDTO,
        InvalidRequestSortListError
      );
      const name = getOptionalString(dto, "name", InvalidRequestNameError);
      const emailAddress = getOptionalString(dto, "emailAddress", InvalidRequestEmailAddressError);
      const projectId = getOptionalString(dto, "projectId", InvalidRequestProjectIdError);
      const addedOnStartDate = getOptionalLeoDate(dto, "addedOnStartDate", InvalidRequestAddedOnStartDateError);
      const addedOnEndDate = getOptionalLeoDate(dto, "addedOnEndDate", InvalidRequestAddedOnEndDateError);
      const expiresOnStartDate = getOptionalLeoDate(dto, "expiresOnStartDate", InvalidRequestExpiresOnStartDateError);
      const expiresOnEndDate = getOptionalLeoDate(dto, "expiresOnEndDate", InvalidRequestExpiresOnEndDateError);
      const itemsPerPage = SpocManagementItemsPerPage.fromDTO(getObject(dto, "itemsPerPage", InvalidRequestItemsPerPageError));
      const pageIndex = SpocManagementPageIndex.fromDTO(getObject(dto, "pageIndex", InvalidRequestPageIndexError));
      return new Request(
        sortList,
        name,
        emailAddress,
        projectId,
        addedOnStartDate,
        addedOnEndDate,
        expiresOnStartDate,
        expiresOnEndDate,
        itemsPerPage,
        pageIndex
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          sortList: this.sortList.map((e: GetProjectSpocsSortOrder) => { return e.toDTO() }),
          itemsPerPage: this.itemsPerPage.toDTO(),
          pageIndex: this.pageIndex.toDTO()
      };
      if (this.name !== undefined && this.name !== null){
        dto.name = this.name;
      }
      if (this.emailAddress !== undefined && this.emailAddress !== null){
        dto.emailAddress = this.emailAddress;
      }
      if (this.projectId !== undefined && this.projectId !== null){
        dto.projectId = this.projectId;
      }
      if (this.addedOnStartDate !== undefined && this.addedOnStartDate !== null){
        dto.addedOnStartDate = this.addedOnStartDate.toDTO();
      }
      if (this.addedOnEndDate !== undefined && this.addedOnEndDate !== null){
        dto.addedOnEndDate = this.addedOnEndDate.toDTO();
      }
      if (this.expiresOnStartDate !== undefined && this.expiresOnStartDate !== null){
        dto.expiresOnStartDate = this.expiresOnStartDate.toDTO();
      }
      if (this.expiresOnEndDate !== undefined && this.expiresOnEndDate !== null){
        dto.expiresOnEndDate = this.expiresOnEndDate.toDTO();
      }
      return dto;
    }
    private static validateName = (name: string): void => {
      if(name.length < 1) {
        throw new InvalidRequestNameMinSizeError(
          1,
          `Attribute name is too short. Size is ${name.length}. Min size is 1.`
        );
      }
      if(name.length > 200) {
        throw new InvalidRequestNameMaxSizeError(
          200,
          `Attribute name is too long. Size is ${name.length}. Max size is 200.`
        );
      }
        if(name.trim().length === 0) {
          throw new InvalidRequestNameNonEmptyError(
            `Attribute name cannot be an empty string.`
          );
        }
    }
    private static validateEmailAddress = (emailAddress: string): void => {
      if(emailAddress.length < 1) {
        throw new InvalidRequestEmailAddressMinSizeError(
          1,
          `Attribute emailAddress is too short. Size is ${emailAddress.length}. Min size is 1.`
        );
      }
      if(emailAddress.length > 200) {
        throw new InvalidRequestEmailAddressMaxSizeError(
          200,
          `Attribute emailAddress is too long. Size is ${emailAddress.length}. Max size is 200.`
        );
      }
        if(emailAddress.trim().length === 0) {
          throw new InvalidRequestEmailAddressNonEmptyError(
            `Attribute emailAddress cannot be an empty string.`
          );
        }
    }
    private static validateProjectId = (projectId: string): void => {
      if(projectId.length < 3) {
        throw new InvalidRequestProjectIdMinSizeError(
          3,
          `Attribute projectId is too short. Size is ${projectId.length}. Min size is 3.`
        );
      }
      if(projectId.length > 200) {
        throw new InvalidRequestProjectIdMaxSizeError(
          200,
          `Attribute projectId is too long. Size is ${projectId.length}. Max size is 200.`
        );
      }
        if(projectId.trim().length === 0) {
          throw new InvalidRequestProjectIdNonEmptyError(
            `Attribute projectId cannot be an empty string.`
          );
        }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetProjectSpocsRPC(sortList='${this.sortList}',name='${this.name}',emailAddress='${this.emailAddress}',projectId='${this.projectId}',addedOnStartDate='${this.addedOnStartDate}',addedOnEndDate='${this.addedOnEndDate}',expiresOnStartDate='${this.expiresOnStartDate}',expiresOnEndDate='${this.expiresOnEndDate}',itemsPerPage='${this.itemsPerPage}',pageIndex='${this.pageIndex}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    getProjectSpocsPaginationResponse: GetProjectSpocsPaginationResponseDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetProjectSpocsResponse: boolean = true;
  
    readonly getProjectSpocsPaginationResponse: GetProjectSpocsPaginationResponse[];
    readonly totalItems: number;
  
  
    constructor(
    getProjectSpocsPaginationResponse: GetProjectSpocsPaginationResponse[], 
    totalItems: number
    ) {
      this.getProjectSpocsPaginationResponse = getProjectSpocsPaginationResponse;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const getProjectSpocsPaginationResponse = getList<GetProjectSpocsPaginationResponseDTO, GetProjectSpocsPaginationResponse>(
        dto,
        "getProjectSpocsPaginationResponse",
        GetProjectSpocsPaginationResponse.fromDTO,
        InvalidResponseGetProjectSpocsPaginationResponseError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        getProjectSpocsPaginationResponse,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          getProjectSpocsPaginationResponse: this.getProjectSpocsPaginationResponse.map((e: GetProjectSpocsPaginationResponse) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetProjectSpocs(getProjectSpocsPaginationResponse='${this.getProjectSpocsPaginationResponse}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidDateRange = "INVALID_DATE_RANGE"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetProjectSpocsRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidDateRangeDTO {
      code: string;
    }
    
    export class InvalidDateRange extends LeoRPCError<RPCError> {
      readonly isInvalidDateRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidDateRange;
        super(code);
      }
    
    
      toDTO(): InvalidDateRangeDTO {
        const dto: InvalidDateRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidDateRange.prototype.toString = function toString(): string {
      return `GetProjectSpocsRPC.INVALID_DATE_RANGE()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidDateRange;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidDateRangeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidDateRange:
          return new InvalidDateRange();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSortListError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortListError";
  }
}

export class InvalidRequestNameError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameError";
  }
}

export class InvalidRequestNameMaxSizeError extends InvalidRequestNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameMaxSizeError";
  }
}

export class InvalidRequestNameMinSizeError extends InvalidRequestNameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameMinSizeError";
  }
}

export class InvalidRequestNameNonEmptyError extends InvalidRequestNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestNameNonEmptyError";
  }
}

export class InvalidRequestEmailAddressError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressError";
  }
}

export class InvalidRequestEmailAddressMaxSizeError extends InvalidRequestEmailAddressError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMaxSizeError";
  }
}

export class InvalidRequestEmailAddressMinSizeError extends InvalidRequestEmailAddressError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMinSizeError";
  }
}

export class InvalidRequestEmailAddressNonEmptyError extends InvalidRequestEmailAddressError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressNonEmptyError";
  }
}

export class InvalidRequestProjectIdError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestProjectIdMaxSizeError extends InvalidRequestProjectIdError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdMaxSizeError";
  }
}

export class InvalidRequestProjectIdMinSizeError extends InvalidRequestProjectIdError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdMinSizeError";
  }
}

export class InvalidRequestProjectIdNonEmptyError extends InvalidRequestProjectIdError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdNonEmptyError";
  }
}

export class InvalidRequestAddedOnStartDateError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnStartDateError";
  }
}

export class InvalidRequestAddedOnEndDateError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnEndDateError";
  }
}

export class InvalidRequestExpiresOnStartDateError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestExpiresOnStartDateError";
  }
}

export class InvalidRequestExpiresOnEndDateError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestExpiresOnEndDateError";
  }
}

export class InvalidRequestItemsPerPageError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestPageIndexError extends GetProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidResponseGetProjectSpocsPaginationResponseError extends GetProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseGetProjectSpocsPaginationResponseError";
  }
}

export class InvalidResponseTotalItemsError extends GetProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidDateRangeError extends GetProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidDateRangeError";
  }
}

